import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";

import { PendingPostCard } from "@/components/approvals/PendingPostCard";
import { formatRelativeTime } from "@/lib/formatters";
import { isAuthenticated, pb } from "@/lib/pb";

interface PendingPost {
  id: string;
  run_id: string;
  slug: string;
  title_es: string;
  category: string;
  status: string;
  audit_score: number | null;
  created: string;
}

export const Route = createFileRoute("/approvals/")({
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: "/login" });
  },
  component: ApprovalsList,
});

function ApprovalsList() {
  const { data, refetch } = useQuery({
    queryKey: ["pending_posts"],
    queryFn: async () => {
      const res = await pb.collection("pending_posts").getList<PendingPost>(1, 50, {
        filter: 'status = "pending" || status = "failed"',
        sort: "-created",
      });
      return res.items;
    },
  });

  // Realtime: any change to pending_posts refetches the list.
  useEffect(() => {
    const unsub = pb
      .collection("pending_posts")
      .subscribe("*", () => refetch())
      .catch(() => () => {});
    return () => {
      Promise.resolve(unsub).then((fn) => typeof fn === "function" && fn());
    };
  }, [refetch]);

  const items = data ?? [];

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div>
        <p className="path-chip">/approvals</p>
        <div className="flex items-center gap-3 mt-1">
          <h1 className="text-3xl font-semibold">Pending</h1>
          <span className="font-mono text-sm text-muted">
            [{items.length.toString().padStart(2, "0")}]
          </span>
        </div>
      </div>

      <AnimatePresence mode="popLayout">
        {items.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="surface p-16 text-center text-muted"
          >
            <p className="font-mono text-sm">$ no pending posts</p>
            <p className="mt-2 text-xs">
              Los nuevos borradores del pipeline aparecen aquí en tiempo real.
            </p>
          </motion.div>
        ) : (
          <motion.div
            layout
            className="grid gap-3"
            initial="hidden"
            animate="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }}
          >
            {items.map((p) => (
              <Link
                key={p.id}
                to="/approvals/$id"
                params={{ id: p.id }}
                className="block focus:outline-none"
              >
                <PendingPostCard
                  slug={p.slug}
                  title={p.title_es || p.slug}
                  category={p.category}
                  score={p.audit_score}
                  createdAgo={formatRelativeTime(p.created)}
                />
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
